import "react-datepicker/dist/react-datepicker.css";
import {
  Badge,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { getConfigWithToken } from "../config/config";
import { toast, ToastContainer } from "react-toastify";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import formatDate from "../utils/formatDate";
import { createPortal } from "react-dom";
import SessionHistoryPDFGenerator from "../outlets/SessionHistoryPDFGenerator";
import FollowUpPDFGenerator from "../outlets/FollowUpPDFGenerator";
import { getPaymentMethod, getVerificationStatus, extractAccessCode } from "../components/UpcomingSessions/utils";
import SessionDetailsDrawer from "../components/UpcomingSessions/SessionDetailsDrawer";

import { FaSearch } from "react-icons/fa";
const SessionHistoryAdmin = () => {
  const [appointmentsPerPage, setAppointmentsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginateAppointments, setPaginateAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSessionDetail, setShowSessionDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSessionForModal, setSelectedSessionForModal] = useState(null);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [selectedSessionForNoShow, setSelectedSessionForNoShow] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSessionForCancel, setSelectedSessionForCancel] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedSessionForComplete, setSelectedSessionForComplete] = useState(null);
  const [sessionHistoryDetailsBySession, setSessionHistoryDetailsBySession] = useState({});
  const [loadingSessionDetails, setLoadingSessionDetails] = useState({});
  const [paymentDataBySession, setPaymentDataBySession] = useState({});

  const user = useSelector((state) => state?.user);
  const navigate = useNavigate();
  const location = useLocation();

  const clearSearchFilter = () => {
    setDebouncedSearch("");
    setSearch("");
  };

  const closeSessionDetail = () => {
    setShowSessionDetail(false);
    setSelectedSession(null);
    setSummaryData(null);
  };

  const fetchSessionHistoryDetailsForSessions = async (sessionsList) => {
    try {
      const sessionIds = (sessionsList || [])
        .map((session) => session._id || session.id)
        .filter(Boolean);

      if (sessionIds.length === 0) {
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-sessions`,
        { sessionIds },
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success && response.data.data) {
        const detailsMap = {};
        response.data.data.forEach((detail) => {
          if (detail.sessionId) {
            detailsMap[detail.sessionId] = detail;
          }
        });

        setSessionHistoryDetailsBySession((prev) => ({
          ...prev,
          ...detailsMap,
        }));
      }
    } catch (error) {
      console.error("-> -> fetchSessionHistoryDetailsForSessions -> error->", error);
    }
  };

  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const params = {};

      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };

      if (selectedStartDate && selectedEndDate) {
        params.startDate = formatDate(selectedStartDate);
        params.endDate = formatDate(selectedEndDate);

      } else if (selectedStartDate && !selectedEndDate) {
        params.date = formatDate(selectedStartDate);
      }

      if (debouncedSearch && debouncedSearch.trim() !== "") {
        params.search = debouncedSearch.trim();
        params.searchAll = true;
      }

      // Server-side pagination: returns only current page rows.
      params.limit = appointmentsPerPage;
      params.skip = (currentPage - 1) * appointmentsPerPage;
      params.lightweight = true;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/appointment/getSessionHistory`,
        { params, ...getConfigWithToken(user.user?.token) }
      );

      if (response.data.success) {
        const sessionsData = response.data.data ?? [];

        const transformedSessions = sessionsData.map((session) => ({
          ...session,
          _id: session._id ?? session.id ?? Math.random().toString(),
          appointmentdate:
            session.appointmentdate ?? session.date ?? session.appointmenttime,
          appointmenttime:
            session.appointmenttime ?? session.appointmentdate ?? session.date,
          status: session.status ?? "Scheduled",
        }));

        setSessions(transformedSessions);
        const serverTotal =
          response.data.total ?? response.data.totalCount ?? null;
        setTotalSessions(
          Number.isFinite(serverTotal) ? serverTotal : transformedSessions.length
        );
        const paymentDataMap = {};
        transformedSessions.forEach((session) => {
          const sessionId = session._id || session.id;
          if (sessionId && session.paymentData) {
            paymentDataMap[sessionId] = session.paymentData;
          }
        });
        setPaymentDataBySession((prev) => ({ ...prev, ...paymentDataMap }));
        fetchSessionHistoryDetailsForSessions(transformedSessions);
      } else {
        toast.error("Failed to fetch sessions data.");
      }
    } catch (error) {
      console.error("-> -> fetchSessions -> error->", error);

      toast.error(error?.response?.data?.message);
    } finally {
      setSessionsLoading(false);
    }
  };

  const getActualStatus = (session) => {
    const sessionId = session._id || session.id;
    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
    const now = new Date();

    const appointmentEndTime = new Date(session.appointmentEndTime);
    const appointmentStartTime = new Date(session.appointmenttime);

    const hasValidStart = !isNaN(appointmentStartTime.getTime());
    const hasValidEnd =
      appointmentEndTime !== null && !isNaN(appointmentEndTime.getTime());

    if (session.status === "Canceled by Super Admin" || session.status === "System Cancel") {
      return "Admin Cancel";
    }

    if (session.status === "late_cancelled") {
      return "Late Cancel";
    }

    if (session.status === "No Show") {
      return "No Show";
    }

    if (session.status === "Canceled by Provider") {
      return "Provider Cancel";
    }

    if (session.status === "canceled_by_user" || session.status === "Cancelled" || session.status === "Canceled") {
      return "User Cancel";
    }

    if (session.status === "Completed" || sessionDetail?.isSigned === true) {
      return "Completed";
    }

    if (hasValidStart && now >= appointmentStartTime) {
      if (hasValidEnd && now > appointmentEndTime) {
        return sessionDetail?.isSigned === true ? "Completed" : "Pending";
      }
      return "Pending";
    }

    return session.status === "Completed" ? "Completed" : "Pending";
  };
  const getFilteredSessions = () => {
    let filtered = sessions;

    filtered = filtered.filter((session) => {
      const appointmentTime = session.appointmenttime || session.appointmentdate || session.date;
      if (!appointmentTime) return false;
      const sessionDate = new Date(appointmentTime);
      return sessionDate < new Date();
    });

    if (debouncedSearch && debouncedSearch.trim() !== "") {
      const searchTerm = debouncedSearch.trim().toLowerCase();
      filtered = filtered.filter((session) => {
        const clientName = `${session?.user?.firstname || ""} ${session?.user?.lastname || ""}`.toLowerCase();
        const providerName = `${session?.health_specialist?.firstname || ""} ${session?.health_specialist?.lastname || ""}`.toLowerCase();
        const sessionId = (session.appointment_id || session._id || "").toLowerCase();

        return (
          clientName.includes(searchTerm) ||
          providerName.includes(searchTerm) ||
          sessionId.includes(searchTerm)
        );
      });
    }

    if (statusFilter !== "All") {
      filtered = filtered.filter((session) => {
        const actualStatus = getActualStatus(session);

        return actualStatus === statusFilter;
      });
    }

    return filtered;
  };

  const getStatusBadge = (session) => {
    const actualStatus = getActualStatus(session);

    const statusConfig = {
      Completed: {
        bg: "#D0FAE5",
        color: "#006145",
        text: "Completed",
        tooltip: "Documentation completed and ready for billing."
      },
      Pending: {
        bg: "#FFFED8",
        color: "#687000",
        text: "Pending",
        tooltip: "Provider documentation incomplete."
      },
      "Late Cancel": {
        bg: "#FEE9C6",
        color: "#973B00",
        text: "Late Cancel",
        tooltip: "Canceled within 12 hours — fee may apply."
      },
      "No Show": {
        bg: "#FEE4E6",
        color: "#ED3A3A",
        text: "No Show",
        tooltip: "Client missed session — fee may apply."
      },
      "User Cancel": {
        bg: "#EDE9FE",
        color: "#5D0EC1",
        text: "User Cancel",
        tooltip: "Canceled by patient in acceptable timeframe or canceled by provider"
      },
      "Provider Cancel": {
        bg: "#C6E0FE",
        color: "#002697",
        text: "Provider Cancel",
        tooltip: "Provider canceled the session"
      },
      "Admin Cancel": {
        bg: "#000000",
        color: "#FFFFFF",
        text: "Admin Cancel",
        tooltip: "Canceled by Super Admin or System."
      },
      Missed: {
        bg: "#FEE4E6",
        color: "#ED3A3A",
        text: "No Show",
        tooltip: "Client missed session — fee may apply."
      },
      Cancel: {
        bg: "#EDE9FE",
        color: "#5D0EC1",
        text: "User Cancel",
        tooltip: "Canceled by patient in acceptable timeframe or canceled by provider"
      },
      Canceled: {
        bg: "#EDE9FE",
        color: "#5D0EC1",
        text: "User Cancel",
        tooltip: "Canceled by patient in acceptable timeframe or canceled by provider"
      },
    };

    const config = statusConfig[actualStatus] ?? {
      bg: "#FEF3C7",
      color: "#92400E",
      text: actualStatus ?? "Pending",
      tooltip: "Status information",
      variant: "secondary",
    };

    return (
      <Badge
        bg={config.bg}
        pill
        style={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: "500",
          height: "19px",
          width: "92px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: "1",
          padding: "0",
        }}>
        {config.text}
      </Badge>
    );
  };

  const getFeeStatusBadge = (session) => {
    const actualStatus = getActualStatus(session);
    const isNoShow = actualStatus === "No Show";
    const isLateCancel = actualStatus === "Late Cancel";
    if (!isNoShow && !isLateCancel) {
      return null;
    }
    const hasNoShowFee = session.no_show_fee === true;
    const hasLateCancelFee = session.late_cancel_fee === true;
    const isWaived = session.waive_fee === true;
    if (isWaived) {
      return (
        <span
          style={{
            backgroundColor: "#EDE9FE",
            color: "#5D0EC1",
            fontWeight: "500",
            padding: "6px 12px",
            fontSize: "12px",
            borderRadius: "50px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "1",
            minWidth: "80px",
            textAlign: "center",
          }}>
          Waived
        </span>
      );
    }

    if (hasNoShowFee || hasLateCancelFee) {
      return (
        <span
          style={{
            backgroundColor: "#D0FAE5",
            color: "#006145",
            fontWeight: "500",
            padding: "6px 12px",
            fontSize: "12px",
            borderRadius: "50px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "1",
            minWidth: "80px",
            textAlign: "center",
          }}>
          Applied
        </span>
      );
    }
    return (
      <span
        style={{
          backgroundColor: "#FFFED8",
          color: "#687000",
          fontWeight: "500",
          padding: "6px 12px",
          fontSize: "12px",
          borderRadius: "50px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: "1",
          minWidth: "80px",
          textAlign: "center",
        }}>
        Pending
      </span>
    );
  };

  const handleRangeSelect = (dates) => {
    if (!dates) {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setCurrentPage(1);
      return;
    }

    const [start, end] = dates;

    setSelectedEndDate(end);
    setSelectedStartDate(start);

    if (end) setShowDatePicker(false);
  };

  const handleViewDetails = async (session) => {
    try {
      const appointmentId = session._id || session.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        return;
      }

      if (paymentDataBySession[appointmentId]) {
      } else if (session.paymentData) {
        setPaymentDataBySession((prev) => ({
          ...prev,
          [appointmentId]: session.paymentData,
        }));
      } else {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/payments/get-by-session/${appointmentId}`,
            getConfigWithToken(user.user?.token)
          );

          if (response.data.success && response.data.hasPayment) {
            setPaymentDataBySession((prev) => ({
              ...prev,
              [appointmentId]: response.data.data,
            }));
          } else {
            setPaymentDataBySession((prev) => ({
              ...prev,
              [appointmentId]: null,
            }));
          }
        } catch (paymentError) {
          console.error("Error fetching payment details:", paymentError);
          setPaymentDataBySession((prev) => ({
            ...prev,
            [appointmentId]: null,
          }));
        }
      }

      // Always show the modal regardless of payment status
      setSelectedSessionForModal(session);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error in handleViewDetails:", error);
      toast.error("Error loading session details");
    }
  };

  const handleReviewNote = (session) => {
    const fromPath = `${location.pathname}${location.search}`;
    if (session?.appointmentType === "Follow-Up") {
      navigate("/follow-up", {
        state: { session, from: fromPath },
      });
    } else {
      navigate("/session-details", {
        state: { session, from: fromPath },
      });
    }
  };

  const handleCompleteNote = (session) => {
    setSelectedSessionForComplete(session);
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setSelectedSessionForComplete(null);
  };

  const handleConfirmComplete = async () => {
    try {
      if (!selectedSessionForComplete) {
        toast.error("Session not found");
        closeCompleteModal();
        return;
      }

      const appointmentId = selectedSessionForComplete._id || selectedSessionForComplete.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        closeCompleteModal();
        return;
      }
      try {
        const sessionHistoryResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
          {
            sessionId: appointmentId,
            isSigned: true,
            isAutoSave: true,
            carePlanAction: "create"
          },
          getConfigWithToken(user.user?.token)
        );

        if (!sessionHistoryResponse.data.success) {
          console.warn("Session history update warning:", sessionHistoryResponse.data.message);
        }
      } catch (sessionHistoryError) {
        console.error("Error creating/updating session history:", sessionHistoryError);
      }

      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "Completed",
        },
        getConfigWithToken(user.user?.token)
      );

      if (updateResponse.data.success) {
        toast.success("Session marked as completed");
        closeCompleteModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
      }
    } catch (error) {
      console.error("Error completing session:", error);
      toast.error(error?.response?.data?.message || "Failed to complete session");
    }
  };

  const handleMarkNoShow = (session) => {
    setSelectedSessionForNoShow(session);
    setShowNoShowModal(true);
  };

  const closeNoShowModal = () => {
    setShowNoShowModal(false);
    setSelectedSessionForNoShow(null);
  };

  const handleWaiveFee = async () => {
    try {
      if (!selectedSessionForNoShow) {
        toast.error("Session not found");
        closeNoShowModal();
        return;
      }

      const appointmentId = selectedSessionForNoShow._id || selectedSessionForNoShow.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        closeNoShowModal();
        return;
      }

      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "No Show",
          waive_fee: true,
        },
        getConfigWithToken(user.user?.token)
      );

      if (updateResponse.data.success) {
        toast.success("Fee waived and session marked as No Show");
        closeNoShowModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
        closeNoShowModal();
      }
    } catch (error) {
      console.error("Error waiving fee:", error);
      toast.error(error?.response?.data?.message || "Failed to waive fee");
      closeNoShowModal();
    }
  };

  const handleApplyFee = async () => {
    try {
      if (!selectedSessionForNoShow) {
        toast.error("Session not found");
        closeNoShowModal();
        return;
      }

      const appointmentId = selectedSessionForNoShow._id || selectedSessionForNoShow.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        closeNoShowModal();
        return;
      }

      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "No Show",
          no_show_fee: true,
        },
        getConfigWithToken(user.user?.token)
      );
      if (updateResponse.data.success) {
        toast.success("No-show fee applied and session marked as No Show");
        closeNoShowModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
        closeNoShowModal();
      }
    } catch (error) {
      console.error("Error applying fee:", error);
      toast.error(error?.response?.data?.message || "Failed to apply fee");
      closeNoShowModal();
    }
  };

  const handleMarkCanceled = (session) => {
    setSelectedSessionForCancel(session);
    setShowCancelModal(true);
  };

  const handleApplyLateCancelFee = async (session) => {
    try {
      if (!session) {
        toast.error("Session not found");
        return;
      }

      const appointmentId = session._id || session.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-late-cancel-fee-status/${appointmentId}`,
        {},
        getConfigWithToken(user.user?.token)
      );
      if (response.data.success) {
        toast.success("Late cancel fee applied successfully");
        fetchSessions();
      } else {
        toast.error(response.data.message || "Failed to apply late cancel fee");
      }
    } catch (error) {
      console.error("Error applying late cancel fee:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to apply late cancel fee"
      );
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSelectedSessionForCancel(null);
  };

  const updateSessionStatus = async (session, status, successMessage, closeModal) => {
    try {
      if (!session) {
        toast.error("Session not found");
        if (closeModal) closeModal();
        return;
      }

      const appointmentId = session._id || session.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        if (closeModal) closeModal();
        return;
      }

      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: status,
        },
        getConfigWithToken(user.user?.token)
      );

      if (updateResponse.data.success) {
        toast.success(successMessage);
        if (closeModal) closeModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
      }
    } catch (error) {
      console.error(`Error updating session status to ${status}:`, error);
      toast.error(error?.response?.data?.message || `Failed to update session status`);
      if (closeModal) closeModal();
    }
  };

  const handleConfirmCancel = async () => {
    await updateSessionStatus(
      selectedSessionForCancel,
      "System Cancel",
      "Session marked as canceled",
      closeCancelModal
    );
  };

  const calculateDropdownPosition = useCallback((buttonRect, dropdownHeight = 160, dropdownWidth = 160) => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top, left;
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      top = buttonRect.top - dropdownHeight - 4;
      if (top < 0) {
        top = 4;
      }
    } else {
      top = buttonRect.bottom + 4;
      if (top + dropdownHeight > viewportHeight) {
        top = viewportHeight - dropdownHeight - 4;
        if (top < buttonRect.top - dropdownHeight) {
          top = Math.max(4, buttonRect.top - dropdownHeight - 4);
        }
      }
    }

    left = buttonRect.left + buttonRect.width / 2;
    if (left - dropdownWidth / 2 < 0) {
      left = dropdownWidth / 2 + 4;
    } else if (left + dropdownWidth / 2 > viewportWidth) {
      left = viewportWidth - dropdownWidth / 2 - 4;
    }

    return { top, left };
  }, []);

  const fetchSessionHistoryDetailBySessionId = async (sessionId) => {
    try {
      if (!sessionId) {
        return null;
      }

      if (sessionHistoryDetailsBySession[sessionId]) {
        return sessionHistoryDetailsBySession[sessionId];
      }

      setLoadingSessionDetails((prev) => ({
        ...prev,
        [sessionId]: true,
      }));

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-session/${sessionId}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const detail = response.data.data;

        setSessionHistoryDetailsBySession((prev) => ({
          ...prev,
          [sessionId]: detail,
        }));

        setLoadingSessionDetails((prev) => {
          const newState = { ...prev };
          delete newState[sessionId];
          return newState;
        });

        return detail;
      }
    } catch (error) {
      console.error("-> -> fetchSessionHistoryDetailBySessionId -> error->", error);
      setLoadingSessionDetails((prev) => {
        const newState = { ...prev };
        delete newState[sessionId];
        return newState;
      });
      return null;
    }
  };

  const handleDropdownToggle = useCallback((sessionId, event) => {
    if (openDropdownId === sessionId) {
      setOpenDropdownId(null);
      dropdownButtonRef.current = null;
    } else {
      const button = event.currentTarget;
      dropdownButtonRef.current = button;
      const rect = button.getBoundingClientRect();

      const position = calculateDropdownPosition(rect);
      setDropdownPosition(position);
      setOpenDropdownId(sessionId);

      fetchSessionHistoryDetailBySessionId(sessionId);
    }
  }, [openDropdownId, calculateDropdownPosition, user.user?.token]);

  const handleDropdownItemClick = (handler, session) => {
    handler(session);
    setOpenDropdownId(null);
    dropdownButtonRef.current = null;
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const filteredSessions = getFilteredSessions();

  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      setShowDatePicker(false);
    }
  }, [selectedStartDate, selectedEndDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    // When filters/search change, reset to page 1.
    setCurrentPage(1);
  }, [debouncedSearch, selectedEndDate, selectedStartDate]);

  useEffect(() => {
    fetchSessions();
  }, [debouncedSearch, selectedEndDate, selectedStartDate, currentPage, appointmentsPerPage]);

  useEffect(() => {
    if (showAllSessions) {
      setPaginateAppointments(filteredSessions);
    } else {
      setPaginateAppointments(filteredSessions.slice(0, appointmentsPerPage));
    }
  }, [appointmentsPerPage, filteredSessions, showAllSessions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.session-dropdown-button') && !event.target.closest('.session-dropdown-menu')) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && openDropdownId) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      }
    };

    let scrollTimeout;
    const handleScroll = () => {
      if (openDropdownId && dropdownButtonRef.current) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const rect = dropdownButtonRef.current.getBoundingClientRect();
          const position = calculateDropdownPosition(rect);
          setDropdownPosition(position);
        }, 10);
      }
    };

    const handleResize = () => {
      if (openDropdownId && dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        const position = calculateDropdownPosition(rect);
        setDropdownPosition(position);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        clearTimeout(scrollTimeout);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [openDropdownId, calculateDropdownPosition]);

  return (
    <div className="app-page session-history-container">
      <ToastContainer />

      {!showSessionDetail ? (
        <Card
          style={{
            border: "none",
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            height: "100%",
            overflow: "hidden",
            maxWidth: "100%",
            width: "100%",
            boxSizing: "border-box",
          }}>
          <Card.Body className="p-0" style={{ overflow: "hidden", maxWidth: "100%" }}>
            <div className="app-page-toolbar mb-0" style={{ width: "100%", gap: "10px" }}>
              <div className="flex-shrink-0">
                <h3 className="app-page-title">
                  Session History
                </h3>
              </div>

              <div className="d-flex gap-2 flex-nowrap align-items-center flex-shrink-0 app-page-toolbar-filters">
                <div className="app-filter-search-wrap" style={{ width: "180px", minWidth: "180px" }}>
                  <FaSearch className="app-filter-search-icon" />
                  <input
                    className="form-control app-filter-search"
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    style={{
                      width: "100%",
                      paddingRight: search ? "30px" : undefined,
                    }}
                    type="text"
                    value={search}
                  />

                  {search && (
                    <i
                      className="fas fa-times position-absolute"
                      onClick={clearSearchFilter}
                      style={{
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "12px",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 2,
                      }}
                    />
                  )}
                </div>

                <div
                  className="position-relative date-picker-container"
                  style={{
                    minWidth: selectedStartDate && selectedEndDate ? "220px" : "180px",
                    width: selectedStartDate && selectedEndDate ? "220px" : "180px"
                  }}>
                  <Button
                    className="custom-date-button"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    variant="outline-secondary"
                    style={{
                      fontSize: "14px",
                      padding: "6px 35px 6px 12px",
                      height: "45px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      backgroundColor: "#E9EEF1",
                      border: "none",
                      color: "#064260",
                      fontWeight: "500",
                      textAlign: "left",
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      position: "relative",
                    }}>
                    {selectedStartDate && selectedEndDate
                      ? `${formatDate(selectedStartDate)} - ${formatDate(selectedEndDate)}`
                      : selectedStartDate
                        ? formatDate(selectedStartDate)
                        : "Date"}
                    {!selectedStartDate && !selectedEndDate && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#064260",
                          pointerEvents: "none",
                        }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    )}
                    {(selectedStartDate || selectedEndDate) && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRangeSelect(null);
                          setShowDatePicker(true);
                        }}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#064260",
                          cursor: "pointer",
                        }}>
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    )}
                  </Button>

                  {showDatePicker && (
                    <div
                      className="position-absolute"
                      style={{ left: 0, top: "100%", zIndex: 1000, marginTop: "4px" }}>
                      <DatePicker
                        endDate={selectedEndDate}
                        inline
                        isClearable={true}
                        maxDate={new Date()}
                        onChange={(dates) => {
                          handleRangeSelect(dates);
                          if (dates && Array.isArray(dates) && dates[0] && dates[1]) {
                            setShowDatePicker(false);
                          }
                        }}
                        selectsRange
                        startDate={selectedStartDate}
                      />
                    </div>
                  )}
                </div>
                <div className="position-relative" style={{ minWidth: "130px" }}>
                  <select
                    className="form-select custom-select app-filter-select"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    value={statusFilter}
                    style={{
                      fontSize: "14px",
                      padding: "6px 40px 6px 12px",
                      height: "45px",
                      minWidth: "130px",
                      color: "#064260",
                      fontWeight: "500",
                      appearance: "none",
                      WebkitAppearance: "none",
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2 4L6 8L10 4' stroke='%23064260' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 14px center",
                      backgroundSize: "12px 12px",
                    }}>
                    <option value="All">Status</option>

                    <option value="Pending">Pending</option>

                    <option value="Completed">Completed</option>

                    <option value="Late Cancel">Late Cancel</option>

                    <option value="No Show">No Show</option>

                    <option value="User Cancel">User Cancel</option>

                    <option value="Provider Cancel">Provider Cancel</option>

                    <option value="Admin Cancel">Admin Cancel</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="table-responsive" style={{ overflowX: "auto", overflowY: "visible", width: "100%", maxWidth: "100%" }}>
              {sessionsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />

                  <div className="mt-3 text-muted">Loading sessions...</div>
                </div>
              ) : (
                <>
                  <Table hover style={{ marginBottom: 0, width: "100%", tableLayout: "auto" }}>
                    <thead
                      style={{
                        backgroundColor: "#064260",
                        borderRadius: "0",
                        color: "white",
                      }}>
                      <tr>
                        <th
                          style={{
                            
                            padding: "12px 10px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          }}>
                          <i className="fas fa-hashtag me-2"></i>Session ID
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-user me-2"></i>Client
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-user-md me-2"></i>Provider
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-calendar me-2"></i>Date
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-clock me-2"></i>Actual Time
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-info-circle me-2"></i>Status
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-dollar-sign me-2"></i>Fee Status
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-dollar-sign me-2"></i>Amount
                        </th>

                        <th
                          style={{
                            padding: "12px 10px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          }}>
                          <i className="fas fa-cogs me-2"></i>Action
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginateAppointments.length > 0 ? (
                        paginateAppointments.map((session) => {
                          return (
                            <tr
                              key={session._id}
                              style={{ borderBottom: "1px solid #f8f9fa" }}>
                              <td
                                style={{
                                  color: "#2C5F7C",
                                  fontWeight: "600",
                                  padding: "12px 10px",
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  whiteSpace: "nowrap",
                                  fontSize: "13px",
                                }}>
                                {session.appointment_id}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session?.user?.firstname}{" "}
                                {session?.user?.lastname}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session?.health_specialist?.firstname} {session?.health_specialist?.lastname}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session.appointmenttime
                                  ? formatDate(session.appointmenttime, "date")
                                  : session.appointmentdate
                                    ? formatDate(session.appointmentdate, "date")
                                    : "N/A"}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {(() => {
                                  const sessionId = session._id || session.id;
                                  const sessionDetail = sessionHistoryDetailsBySession[sessionId];

                                  const formatTimeString = (timeStr) => {
                                    if (!timeStr) return "";
                                    if (typeof timeStr === "string" && timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
                                      return timeStr;
                                    }
                                    if (typeof timeStr === "string" && timeStr.includes(":")) {
                                      const parts = timeStr.split(":");
                                      if (parts.length >= 2) {
                                        return `${parts[0]}:${parts[1]}`;
                                      }
                                      return timeStr;
                                    }
                                    try {
                                      return formatDate(timeStr, "time");
                                    } catch (e) {
                                      return timeStr;
                                    }
                                  };

                                  if (sessionDetail?.timeIn && sessionDetail?.timeOut) {
                                    return `${formatTimeString(sessionDetail.timeIn)} - ${formatTimeString(
                                      sessionDetail.timeOut
                                    )}`;
                                  }

                                  return "-";
                                })()}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  {getStatusBadge(session)}
                                </div>
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  {getFeeStatusBadge(session)}
                                </div>
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  {session?.paymentData?.paymentMethod === "cash"
                                    ? `$${session?.paymentData?.amount ? Math.floor(session.paymentData.amount) : 0}`
                                    : ""}
                                </div>
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  <button
                                    className="session-dropdown-button"
                                    onClick={(e) => handleDropdownToggle(session._id, e)}
                                    style={{
                                      backgroundColor: "#27B99A",
                                      border: "none",
                                      borderRadius: "6px",
                                      width: "66px",
                                      height: "26px",
                                      padding: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: "66px",
                                      cursor: "pointer",
                                    }}>
                                    <span
                                      style={{
                                        color: "#ffffff",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        margin: 0,
                                        padding: 0,
                                        lineHeight: 1,
                                        display: "block",
                                      }}>
                                      ⋯
                                    </span>
                                  </button>
                                </div>
                              </td>

                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="text-center text-muted py-5"
                            colSpan="9">
                            <div style={{ color: "#6c757d", fontSize: "18px" }}>
                              <i
                                className="fas fa-inbox fa-2x mb-3"
                                style={{ color: "#dee2e6" }}></i>
                              <br />

                              {sessions.length === 0 ? (
                                <>
                                  <strong>No sessions found</strong>

                                  <br />

                                  <small className="text-muted">
                                    No session history available.
                                  </small>
                                </>
                              ) : selectedEndDate ||
                                selectedStartDate ||
                                statusFilter !== "All" ||
                                (search && search.trim() !== "") ? (
                                <>
                                  <strong>
                                    No sessions found for the selected filters
                                  </strong>

                                  <br />

                                  <small className="text-muted">
                                    Try adjusting your search, date, or status
                                    filters.
                                  </small>
                                </>
                              ) : (
                                <>
                                  <strong>No sessions available</strong>

                                  <br />

                                  <small className="text-muted">
                                    Unable to load session data.
                                  </small>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {showAllSessions ? (
                    <div className="d-flex justify-content-end align-items-center py-3 px-4">
                      <Button
                        onClick={() => {
                          setShowAllSessions(false);
                          setPaginateAppointments(
                            filteredSessions.slice(0, appointmentsPerPage)
                          );
                        }}
                        size="sm"
                        style={{
                          backgroundColor: "#27B99A",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: "500",
                          padding: "6px 16px",
                        }}
                        variant="success">
                        View Less
                      </Button>
                    </div>
                  ) : (
                    <TablePaginationBar
                      currentPage={currentPage}
                      totalItems={totalSessions}
                      rowsPerPage={appointmentsPerPage}
                      onPageChange={paginate}
                      onRowsPerPageChange={(n) => {
                        setAppointmentsPerPage(n);
                        setCurrentPage(1);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      leftExtra={
                        <Button
                          onClick={() => {
                            setPaginateAppointments(filteredSessions);
                            setShowAllSessions(true);
                          }}
                          size="sm"
                          style={{
                            backgroundColor: "#27B99A",
                            border: "none",
                            borderRadius: "6px",
                            fontWeight: "500",
                            padding: "6px 16px",
                          }}
                          variant="success">
                          View All
                        </Button>
                      }
                    />
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <div>
          <div className="mb-3">
            <Button
              onClick={closeSessionDetail}
              style={{
                alignItems: "center",
                backgroundColor: "transparent",
                border: "none",
                color: "#27B99A",
                display: "flex",
                fontSize: "16px",
                fontWeight: "500",
                gap: "8px",
                justifyContent: "center",
                padding: "8px 0",
              }}
              variant="button">
              <svg
                fill="none"
                height="28"
                viewBox="0 0 28 28"
                width="28"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M27.3333 14.0001C27.3333 6.64808 21.3519 0.666748 13.9999 0.666747C6.64792 0.666747 0.666588 6.64808 0.666587 14.0001C0.666587 21.3521 6.64792 27.3334 13.9999 27.3334C21.3519 27.3334 27.3333 21.3521 27.3333 14.0001ZM8.11459 14.0001L15.7239 6.39075L17.6093 8.27608L11.8853 14.0001L17.6093 19.7241L15.7239 21.6094L8.11459 14.0001Z"
                  fill="#27B99A"
                />
              </svg>
              Back
            </Button>
          </div>

          <Card
            style={{
              backgroundColor: "#ffffff",
              border: "none",
              height: "100%",
              maxWidth: "80%",
              textAlign: "start",
            }}>
            <Card.Body style={{ padding: "32px" }}>
              <h4
                style={{
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  fontFamily: "Poppins",
                  fontWeight: "700",
                  justifyContent: "start",
                  marginBottom: "32px",
                }}>
                Session: {selectedSession?.appointment_id || "N/A"}
              </h4>

              <div className="mb-4">
                <Row>
                  <Col md={6}>
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Client
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.user?.firstname}{" "}
                        {selectedSession?.user?.lastname}
                      </div>
                    </div>

                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Date
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.appointmenttime
                          ? formatDate(selectedSession.appointmenttime, "date")
                          : selectedSession?.appointmentdate
                            ? formatDate(selectedSession.appointmentdate, "date")
                            : "N/A"}
                      </div>
                    </div>

                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Session Length
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.sessionLength ||
                          selectedSession?.duration}
                        m
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Provider
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.health_specialist?.firstname}{" "}
                        {selectedSession?.health_specialist?.lastname}
                      </div>
                    </div>

                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Scheduled Time
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.appointmenttime
                          ? formatDate(selectedSession.appointmenttime, "time")
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div
                style={{
                  backgroundColor: "#E5E5E5",
                  height: "1px",
                  marginBottom: "32px",
                  width: "100%",
                }}
              />

              {summaryLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />

                  <div className="mt-3 text-muted">
                    Loading session summary...
                  </div>
                </div>
              ) : summaryData ? (
                <div>
                  {summaryData.content && (
                    <div className="mb-4">
                      <h5
                        style={{
                          color: "#000000",
                          fontFamily: "Poppins",
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "16px",
                        }}>
                        Session Summary
                      </h5>

                      <div
                        style={{
                          color: "#000000",
                          whiteSpace: "pre-wrap",
                        }}>
                        {summaryData.content}
                      </div>
                    </div>
                  )}

                  {summaryData.content && (
                    <div
                      style={{
                        backgroundColor: "#E5E5E5",
                        height: "1px",
                        marginBottom: "32px",
                        width: "100%",
                      }}
                    />
                  )}
                  {summaryData.actionItems && (
                    <div>
                      <h5
                        style={{
                          color: "#000000",
                          fontFamily: "Poppins",
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "16px",
                        }}>
                        Action items
                      </h5>

                      <div
                        style={{
                          color: "#000000",
                          whiteSpace: "pre-wrap",
                        }}>
                        {summaryData.actionItems}
                      </div>
                    </div>
                  )}

                  {!summaryData.content && !summaryData.actionItems && (
                    <div className="text-center py-4">
                      <i
                        className="fas fa-file-alt fa-3x mb-3"
                        style={{ color: "#dee2e6" }}></i>

                      <h5 style={{ color: "#6c757d" }}>No Summary Available</h5>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i
                    className="fas fa-exclamation-triangle fa-3x mb-3"
                    style={{ color: "#ffc107" }}></i>

                  <h5 style={{ color: "#6c757d" }}>Error Loading Summary</h5>

                  <p className="text-muted">
                    Unable to load the session summary. Please try again.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      <SessionDetailsDrawer
        showDetailsDrawer={showDetailsModal}
        selectedSession={selectedSessionForModal}
        setShowDetailsDrawer={setShowDetailsModal}
        setSelectedSession={setSelectedSessionForModal}
        paymentDataBySession={paymentDataBySession}
        getActualStatus={getActualStatus}
        sessionHistoryDetailsBySession={sessionHistoryDetailsBySession}
        showSessionStatusSection={true}
      />

      {openDropdownId && (() => {
        const session = paginateAppointments.find(s => s._id === openDropdownId);
        if (!session) return null;

        const sessionId = session._id || session.id;
        const isLoading = loadingSessionDetails[sessionId];
        const hasData = sessionHistoryDetailsBySession[sessionId];

        return createPortal(
          <div
            ref={dropdownMenuRef}
            className="session-dropdown-menu"
            role="menu"
            aria-label="Session actions"
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: "translateX(-50%)",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid #e0e0e0",
              padding: "4px 0",
              minWidth: "170px",
              zIndex: 10000,
              overflow: "visible",
              maxHeight: "none",
              pointerEvents: "auto",
              isolation: "isolate",
            }}
            onClick={(e) => e.stopPropagation()}>
            {(() => {
              if (isLoading && !hasData) {
                return (
                  <div
                    style={{
                      fontSize: "14px",
                      padding: "12px 16px",
                      color: "#666666",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}>
                    <Spinner animation="border" size="sm" style={{ width: "16px", height: "16px" }} />
                    Loading...
                  </div>
                );
              }

              const isLateCancelled = session.status === "late_cancelled";
              const isNoShow = session.status === "No Show" || getActualStatus(session) === "No Show";
              const isCancel = session.status === "Cancel" || getActualStatus(session) === "Cancel";

              if (isNoShow || isCancel) {
                return (
                  <div
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => handleDropdownItemClick(handleViewDetails, session)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDropdownItemClick(handleViewDetails, session);
                      }
                    }}
                    style={{
                      fontSize: "14px",
                      padding: "8px 16px",
                      color: "#000000",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                    View
                  </div>
                );
              }

              if (isLateCancelled) {
                const hasLateCancelFee = session.late_cancel_fee === true;

                return (
                  <>
                    <div
                      role="menuitem"
                      tabIndex={0}
                      onClick={() => handleDropdownItemClick(handleViewDetails, session)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDropdownItemClick(handleViewDetails, session);
                        }
                      }}
                      style={{
                        fontSize: "14px",
                        padding: "8px 16px",
                        color: "#000000",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                      View
                    </div>
                    {!hasLateCancelFee && (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => handleDropdownItemClick(handleApplyLateCancelFee, session)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDropdownItemClick(handleApplyLateCancelFee, session);
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        Apply Late Cancel Fee
                      </div>
                    )}
                  </>
                );
              }

              return (
                <>
                  <div
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => handleDropdownItemClick(handleViewDetails, session)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDropdownItemClick(handleViewDetails, session);
                      }
                    }}
                    style={{
                      fontSize: "14px",
                      padding: "8px 16px",
                      color: "#000000",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                    View
                  </div>
                  {(() => {
                    const sessionId = session._id || session.id;
                    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
                    const isSigned = sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true";

                    const actualStatus = getActualStatus(session);

                    const canShow =
                      actualStatus === "Pending" || isSigned === true;

                    if (!canShow) return null;

                    return (
                      <div
                        role="menuitem"
                        tabIndex={0}

                        onClick={() => {
                          if (isSigned) {
                            handleDropdownItemClick(handleReviewNote, session);
                          } else {
                            handleDropdownItemClick(handleCompleteNote, session);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (isSigned) {
                              handleDropdownItemClick(handleReviewNote, session);
                            } else {
                              handleDropdownItemClick(handleCompleteNote, session);
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        {isSigned ? "Review Note" : "Mark Completed"}
                      </div>
                    );
                  })()}
                  {(() => {
                    const sessionId = session._id || session.id;
                    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
                    const isSigned = sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true";

                    const actualStatus = getActualStatus(session);

                    const canShowNoShow =
                      actualStatus === "Pending" && !isSigned;

                    if (!canShowNoShow) return null;

                    return (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => {
                          if (!isSigned) {
                            handleDropdownItemClick(handleMarkNoShow, session);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!isSigned) {
                              handleDropdownItemClick(handleMarkNoShow, session);
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        {!isSigned && "Mark No-Show"}
                      </div>
                    );
                  })()}
                  {(() => {
                    const sessionId = session._id || session.id;
                    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
                    const isSigned = sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true";

                    const actualStatus = getActualStatus(session);

                    const canShowNoShow =
                      actualStatus === "Pending" && !isSigned;

                    if (!canShowNoShow) return null;

                    return (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => {
                          if (!isSigned) {
                            handleDropdownItemClick(handleMarkCanceled, session);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!isSigned) {
                              handleDropdownItemClick(handleMarkCanceled, session);
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        {!isSigned && "Mark Canceled"}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>,
          document.body
        );
      })()}

      {showNoShowModal && selectedSessionForNoShow && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10002,
            padding: "20px",
          }}
          onClick={closeNoShowModal}>
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e5e5",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2L2 22H22L12 2Z"
                    fill="#FBBF24"
                  />
                  <path
                    d="M12 8V12"
                    stroke="#000000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="16"
                    r="1"
                    fill="#000000"
                  />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#000000",
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: "1.2",
                }}>
                Mark Session as No-Show
              </h3>
            </div>

            {/* Session Information */}
            <div
              style={{
                padding: "24px",
              }}>
              {/* Session Info Card */}
              <div
                style={{
                  backgroundColor: "#F9FAFC",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                }}>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#000000",
                    marginBottom: "8px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {selectedSessionForNoShow?.health_specialist?.firstname || ""}{" "}
                  {selectedSessionForNoShow?.health_specialist?.lastname || ""}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {(() => {
                    const appointmentTime = selectedSessionForNoShow.appointmenttime || selectedSessionForNoShow.appointmentdate;
                    if (!appointmentTime) return "N/A";

                    const date = new Date(appointmentTime);
                    const endTime = selectedSessionForNoShow.appointmentEndTime ? new Date(selectedSessionForNoShow.appointmentEndTime) : null;

                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

                    const month = monthNames[date.getMonth()];
                    const day = date.getDate();
                    const year = date.getFullYear();

                    const startTime = date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });

                    const endTimeStr = endTime ? endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : "";

                    return `${month} ${day}, ${year}${endTimeStr ? ` • ${startTime} – ${endTimeStr}` : ` • ${startTime}`}`;
                  })()}
                </div>
              </div>

              <p
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  marginBottom: "24px",
                  lineHeight: "1.5",
                  fontFamily: "Poppins, sans-serif",
                  margin: "0 0 24px 0",
                }}>
                The client didn't attend this session. Would you like to apply or waive the no-show fee?
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}>
                <button
                  onClick={closeNoShowModal}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#000000",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "80px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleWaiveFee}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#000000",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "100px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  Waive Fee
                </button>
                <button
                  onClick={handleApplyFee}
                  style={{
                    backgroundColor: "#1F2937",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#ffffff",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "100px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#1F2937";
                  }}>
                  Apply Fee
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Canceled Modal */}
      {showCancelModal && selectedSessionForCancel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10002,
            padding: "20px",
          }}
          onClick={closeCancelModal}>
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e5e5",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2L2 22H22L12 2Z"
                    fill="#FBBF24"
                  />
                  <path
                    d="M12 8V12"
                    stroke="#000000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="16"
                    r="1"
                    fill="#000000"
                  />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#000000",
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: "1.2",
                }}>
                Mark Session as Canceled
              </h3>
            </div>

            {/* Session Information */}
            <div
              style={{
                padding: "24px",
              }}>
              <div
                style={{
                  backgroundColor: "#F9FAFC",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                }}>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#1C293D",
                    marginBottom: "8px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {selectedSessionForCancel?.health_specialist?.firstname || ""}{" "}
                  {selectedSessionForCancel?.health_specialist?.lastname || ""}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {(() => {
                    const appointmentTime = selectedSessionForCancel.appointmenttime || selectedSessionForCancel.appointmentdate;
                    if (!appointmentTime) return "N/A";

                    const date = new Date(appointmentTime);
                    const endTime = selectedSessionForCancel.appointmentEndTime ? new Date(selectedSessionForCancel.appointmentEndTime) : null;

                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

                    const month = monthNames[date.getMonth()];
                    const day = date.getDate();
                    const year = date.getFullYear();

                    const startTime = date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });

                    const endTimeStr = endTime ? endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : "";

                    return `${month} ${day}, ${year}${endTimeStr ? ` • ${startTime} – ${endTimeStr}` : ` • ${startTime}`}`;
                  })()}
                </div>
              </div>

              <p
                style={{
                  fontSize: "14px",
                  color: "#324158",
                  marginBottom: "24px",
                  lineHeight: "1.5",
                  fontFamily: "Poppins, sans-serif",
                  margin: "0 0 24px 0",
                }}>
                Are you sure you want to mark this session as canceled?
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}>
                <button
                  onClick={closeCancelModal}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#46566D",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "120px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  No, Go Back
                </button>
                <button
                  onClick={handleConfirmCancel}
                  style={{
                    backgroundColor: "#1F2937",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#ffffff",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "150px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#1F2937";
                  }}>
                  Yes, Cancel Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Session Modal */}
      {showCompleteModal && selectedSessionForComplete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10002,
            padding: "20px",
          }}
          onClick={closeCompleteModal}>
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e5e5",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2L2 22H22L12 2Z"
                    fill="#FBBF24"
                  />
                  <path
                    d="M12 8V12"
                    stroke="#000000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="16"
                    r="1"
                    fill="#000000"
                  />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#000000",
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: "1.2",
                }}>
                Mark Session as Completed
              </h3>
            </div>

            {/* Session Information */}
            <div
              style={{
                padding: "24px",
              }}>
              <div
                style={{
                  backgroundColor: "#F9FAFC",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                }}>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#1C293D",
                    marginBottom: "8px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {selectedSessionForComplete?.health_specialist?.firstname || ""}{" "}
                  {selectedSessionForComplete?.health_specialist?.lastname || ""}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {(() => {
                    const appointmentTime = selectedSessionForComplete.appointmenttime || selectedSessionForComplete.appointmentdate;
                    if (!appointmentTime) return "N/A";

                    const date = new Date(appointmentTime);
                    const endTime = selectedSessionForComplete.appointmentEndTime ? new Date(selectedSessionForComplete.appointmentEndTime) : null;

                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

                    const month = monthNames[date.getMonth()];
                    const day = date.getDate();
                    const year = date.getFullYear();

                    const startTime = date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });

                    const endTimeStr = endTime ? endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : "";

                    return `${month} ${day}, ${year}${endTimeStr ? ` • ${startTime} – ${endTimeStr}` : ` • ${startTime}`}`;
                  })()}
                </div>
              </div>

              <p
                style={{
                  fontSize: "14px",
                  color: "#324158",
                  marginBottom: "24px",
                  lineHeight: "1.5",
                  fontFamily: "Poppins, sans-serif",
                  margin: "0 0 24px 0",
                }}>
                Are you sure you want to mark this session as completed?
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}>
                <button
                  onClick={closeCompleteModal}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#46566D",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "120px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  No, Go Back
                </button>
                <button
                  onClick={handleConfirmComplete}
                  style={{
                    backgroundColor: "#1F2937",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#ffffff",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "150px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#1F2937";
                  }}>
                  Yes, Complete Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistoryAdmin;
